import { useState } from "react";
import { CollectionService } from "@/lib/collectionService";
import { toaster } from "@/lib/toaster";
import type { CollectionIngredient } from "@/types/api";

interface UseRecipeCollectionActionsProps {
  groupId: string | null;
  onDeleteSuccess?: () => void;
}

export function useRecipeCollectionActions({
  groupId,
  onDeleteSuccess,
}: UseRecipeCollectionActionsProps) {
  const [isAddingRecipes, setIsAddingRecipes] = useState(false);
  const [isDeletingGroup, setIsDeletingGroup] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [isRemovingRecipe, setIsRemovingRecipe] = useState(false);
  const [isGeneratingIngredients, setIsGeneratingIngredients] = useState(false);

  const addRecipes = async (recipeIds: string[]): Promise<void> => {
    setIsAddingRecipes(true);
    try {
      await CollectionService.addRecipes(groupId!, recipeIds);
      toaster.create({
        title: "Recipes added",
        description: "Shopping list is being updated...",
      });
      CollectionService.generateIngredients(groupId!).catch((error) => {
        console.error('Failed to generate ingredients:', error);
        toaster.create({
          title: "Failed to update shopping list",
          description: "Please regenerate manually",
        });
      });
    } catch {
      toaster.create({
        title: "Failed to add recipes",
        description: "Please try again",
      });
    } finally {
      setIsAddingRecipes(false);
    }
  };

  const deleteGroup = async (): Promise<void> => {
    setIsDeletingGroup(true);
    try {
      await CollectionService.delete(groupId!);
      onDeleteSuccess?.();
    } finally {
      setIsDeletingGroup(false);
    }
  };

  const updateIngredientStatus = async (ingredientId: string, status: 'have' | 'dont-have'): Promise<void> => {
    // Optimistic update
    const prev = CollectionService.findById(groupId!);
    if (prev) {
      CollectionService.upsert({
        ...prev,
        ingredients: prev.ingredients?.map((ing) =>
          ing.id === ingredientId ? { ...ing, status } : ing
        ) ?? [],
      });
    }
    try {
      await CollectionService.updateIngredientStatus(groupId!, ingredientId, status);
    } catch {
      // Rollback
      if (prev) CollectionService.upsert(prev);
    }
  };

  const reorderIngredients = async (ingredientUpdates: { id: string; index: number }[]): Promise<void> => {
    try {
      await CollectionService.reorderIngredients(groupId!, ingredientUpdates);
    } catch {
      toaster.create({
        title: "Failed to save ingredient order",
        description: "Please try again",
      });
    }
  };

  const removeRecipe = async (recipeId: string): Promise<void> => {
    setIsRemovingRecipe(true);
    try {
      await CollectionService.removeRecipe(groupId!, recipeId);
      toaster.create({
        title: "Recipe removed",
        description: "Shopping list is being updated...",
      });
      CollectionService.generateIngredients(groupId!).catch((error) => {
        console.error('Failed to generate ingredients:', error);
        toaster.create({
          title: "Failed to update shopping list",
          description: "Please regenerate manually",
        });
      });
    } finally {
      setIsRemovingRecipe(false);
    }
  };

  const generateIngredients = async (): Promise<void> => {
    setIsGeneratingIngredients(true);
    try {
      await CollectionService.generateIngredients(groupId!);
      toaster.create({
        title: "Shopping list generated",
        description: "Your shopping list has been updated",
      });
    } catch {
      toaster.create({
        title: "Failed to generate shopping list",
        description: "Please try again",
      });
    } finally {
      setIsGeneratingIngredients(false);
    }
  };

  const rename = async (newTitle: string): Promise<void> => {
    setIsRenaming(true);
    try {
      await CollectionService.update(groupId!, { title: newTitle });
    } catch {
      toaster.create({ title: "Failed to rename collection", description: "Please try again" });
    } finally {
      setIsRenaming(false);
    }
  };

  const handleDeleteGroup = () => {
    if (window.confirm('Delete this entire collection? This cannot be undone.')) {
      void deleteGroup();
    }
  };

  const handleIngredientToggle = (ingredient: CollectionIngredient, hasIt: boolean) => {
    void updateIngredientStatus(ingredient.id, hasIt ? 'have' : 'dont-have');
  };

  const handleRemoveRecipe = (recipeId: string) => {
    if (window.confirm('Remove this recipe from the collection?')) {
      void removeRecipe(recipeId);
    }
  };

  return {
    // Async functions
    addRecipes,
    deleteGroup,
    rename,
    updateIngredientStatus,
    reorderIngredients,
    removeRecipe,
    generateIngredients,
    // Loading state
    isAddingRecipes,
    isDeletingGroup,
    isRenaming,
    isRemovingRecipe,
    isGeneratingIngredients,
    // Helper functions
    handleDeleteGroup,
    handleIngredientToggle,
    handleRemoveRecipe,
  };
}
